/**
 * Sessão da aplicação.
 *
 * O desenho continua o do protótipo, e ele estava certo: **a surface que a
 * pessoa vê é decidida pelo perfil da sessão, nunca pela URL**. O que mudou é
 * de onde a sessão vem — GoTrue quando há servidor, memória quando não há.
 *
 * A leitura é síncrona de propósito: as guardas do roteador rodam a cada
 * navegação e não podem depender de uma ida à rede. O arranque chama
 * `inicializarSessao()` uma vez, e daí em diante `atual()` responde do cache.
 */

import { supabase, temSupabase } from '@/supabase';
import { autenticacaoDemo } from './autenticacaoDemo';
import { autenticacaoSupabase } from './autenticacaoSupabase';
import { definirClinicaAtiva, lembrarClinica } from './clinicaAtiva';
import { definirUnidadeAtiva, guardarUnidade, unidadeGuardada } from './unidadeAtiva';
import { PAPEIS, rotuloPapel } from './papeis';
import type {
    CadastroDeTotp, EstadoDoMfa, Impersonacao, PortaDeAutenticacao, ResultadoDeCadastro, ResultadoLogin, Sessao, Surface,
} from './tipos';

export { PAPEIS, rotuloPapel };
export { SENHA_DEMO, USUARIOS } from './autenticacaoDemo';
export type { Papel, Perfil, Sessao, Surface } from './tipos';

let porta: PortaDeAutenticacao = temSupabase() ? autenticacaoSupabase : autenticacaoDemo;
let cache: Sessao | null = null;
let inicializada = false;

/**
 * A clínica que vai no cabeçalho de cada pedido acompanha a sessão.
 *
 * Só para membro de clínica: o operador não tem vínculo para estreitar, e na
 * impersonação o que vale é a impersonação registrada no banco.
 */
function acompanhar(sessao: Sessao | null): void {
    const clinica = sessao && sessao.perfil === 'clinica' && !sessao.impersonando ? sessao.clinicaId : null;
    definirClinicaAtiva(clinica);
    /* A unidade da topbar volta com a sessão, se foi escolhida nesta clínica. */
    definirUnidadeAtiva(unidadeGuardada(clinica));
}

/** Troca o autenticador. Um lugar só, chamado no arranque ou pelos testes. */
export function definirAutenticacao(nova: PortaDeAutenticacao): void {
    porta = nova;
    cache = null;
    inicializada = false;
    acompanhar(null);
}

/** Lê a sessão gravada. Chamada uma vez, antes de a aplicação montar. */
export async function inicializarSessao(): Promise<Sessao | null> {
    /* Sem clínica no cabeçalho enquanto a sessão se monta: o vínculo é lido do
       banco, e com uma clínica ativa ele devolveria só ela. */
    acompanhar(null);
    cache = await porta.recuperar();
    inicializada = true;
    acompanhar(cache);

    /* Expirar o refresh token no meio do uso deixaria a tela montada com uma
       sessão que o servidor já não aceita. */
    supabase?.auth.onAuthStateChange((evento) => {
        if (evento === 'SIGNED_OUT') {
            cache = null;
            acompanhar(null);
        }
    });

    return cache;
}

export function sessaoInicializada(): boolean {
    return inicializada;
}

export async function entrar(email: string, senha: string): Promise<ResultadoLogin> {
    acompanhar(null);
    const r = await porta.entrar(email, senha);
    if (r.ok) {
        cache = r.sessao;
        inicializada = true;
        acompanhar(cache);
    }
    return r;
}

export async function sair(): Promise<void> {
    cache = null;
    guardarUnidade(null, null);
    acompanhar(null);
    await porta.sair();
}

/**
 * Cria a conta.
 *
 * Não abre sessão nem quando o servidor devolve uma: quem foi convidado entra pelo
 * login, que sabe olhar `membros`; quem não foi volta pelo link do e-mail para
 * `destino`, onde o autocadastro continua.
 */
export async function criarConta(dados: { nome?: string; email: string; senha: string; destino?: string }): Promise<ResultadoDeCadastro> {
    const r = await porta.criarConta(dados);
    if (r.ok) await porta.sair();
    return r;
}

/** Se o botão do Google deve aparecer: só com o provedor ligado no servidor. */
export function googleDisponivel(): Promise<boolean> {
    return porta.googleDisponivel();
}

export function entrarComGoogle(destino: string): Promise<{ ok: boolean; erro?: string }> {
    return porta.entrarComGoogle(destino);
}

export function contaSemClinica(): ReturnType<PortaDeAutenticacao['contaSemClinica']> {
    return porta.contaSemClinica();
}

/**
 * Relê a sessão do servidor. O autocadastro usa depois de abrir a clínica: o login
 * que já existia passa a ter vínculo. Diferente de `inicializarSessao`, não
 * registra outro ouvinte de saída.
 */
export async function recarregarSessao(): Promise<Sessao | null> {
    acompanhar(null);
    cache = await porta.recuperar();
    inicializada = true;
    acompanhar(cache);
    return cache;
}

export function pedirRecuperacao(email: string, destino: string): Promise<{ ok: boolean; erro?: string }> {
    return porta.pedirRecuperacao(email, destino);
}

export function definirNovaSenha(senha: string): Promise<{ ok: boolean; erro?: string }> {
    return porta.definirNovaSenha(senha);
}

/**
 * Entra numa clínica como operador da plataforma.
 *
 * A sessão passa a apontar para o tenant visitado — é dele que os adaptadores
 * leem —, e o perfil continua sendo o de plataforma: quem entrou não virou
 * membro, e a escrita continua recusada pelo banco.
 */
export async function iniciarImpersonacao(clinicaId: string, motivo: string): Promise<Impersonacao> {
    const imp = await porta.iniciarImpersonacao(clinicaId, motivo);
    if (cache) {
        cache = { ...cache, clinica: imp.clinica, clinicaId: imp.clinicaId, impersonando: imp };
    }
    acompanhar(cache);
    return imp;
}

export async function encerrarImpersonacao(): Promise<void> {
    await porta.encerrarImpersonacao();
    if (!cache) return;
    /* Volta a ser o que era: operador sem clínica. */
    const restaurada = { ...cache, clinica: null, clinicaId: null };
    delete restaurada.impersonando;
    cache = restaurada;
    acompanhar(cache);
}

/* O MFA é da CONTA de quem está logado: quem cadastra o autenticador é a própria
   pessoa, e ninguém cadastra por outra. */
export function estadoDoMfa(): Promise<EstadoDoMfa> {
    return porta.estadoDoMfa();
}

export function cadastrarTotp(): Promise<CadastroDeTotp> {
    return porta.cadastrarTotp();
}

export function confirmarTotp(fatorId: string, codigo: string): Promise<void> {
    return porta.confirmarTotp(fatorId, codigo);
}

export function descartarFator(fatorId: string): Promise<void> {
    return porta.descartarFator(fatorId);
}

/** A impersonação viva, se houver — e `undefined` quando o prazo já passou. */
export function impersonacaoAtual(): Impersonacao | undefined {
    const imp = cache?.impersonando;
    if (!imp) return undefined;
    /* Expirada é como se não houvesse: o banco já parou de responder, e a tela
       não pode continuar dizendo que se está dentro. */
    return imp.expiraEm > Date.now() ? imp : undefined;
}

export function atual(): Sessao | null {
    return cache;
}

/** Só para os testes: injeta a sessão sem passar pelo autenticador. */
export function definirSessao(sessao: Sessao | null): void {
    cache = sessao;
    inicializada = true;
    acompanhar(cache);
}

export function limparCache(): void {
    cache = null;
    inicializada = false;
    acompanhar(null);
}

export function pode(permissao: string): boolean {
    const s = cache;
    if (!s) return false;
    return s.permissoes.includes('*') || s.permissoes.includes(permissao);
}

/**
 * O módulo está ligado para esta clínica?
 *
 * A lista vem da sessão, que a leu do banco na entrada: a navegação pergunta
 * isso a cada clique, e ir à rede a cada clique não é opção.
 */
export function moduloLigado(id: string): boolean {
    return !(cache?.modulosDesligados ?? []).includes(id);
}

export function ehPlataforma(): boolean {
    return cache?.perfil === 'plataforma';
}

/** Surface permitida para a sessão atual. */
export function surfacePermitida(desejada?: Surface): Surface {
    const s = cache;
    if (!s) return 'auth';
    if (desejada === 'platform') return s.perfil === 'plataforma' ? 'platform' : 'clinic';
    if (desejada === 'clinic') return s.perfil === 'clinica' || s.impersonando ? 'clinic' : 'platform';
    return s.perfil === 'plataforma' ? 'platform' : 'clinic';
}

/**
 * Troca a clínica ativa sem mexer no caminho: o tenant vem da sessão.
 *
 * Grava a escolha — é ela que a próxima entrada respeita — e passa a mandá-la
 * ao banco em cada pedido, que mostra só a clínica escolhida. Papel e
 * permissões são os do vínculo NAQUELA clínica, então quem chama recarrega a
 * página e a sessão é remontada a partir do banco. Antes a troca só mudava o
 * nome na tela: os dados continuavam os de todas as clínicas.
 */
export function trocarClinica(idOuNome: string): Sessao | null {
    if (!cache) return null;
    const vinculo = cache.vinculos?.find((v) => v.clinicaId === idOuNome)
        ?? cache.vinculos?.find((v) => v.clinica === idOuNome);

    /* Unidade é de uma clínica só: na outra, a escolha volta para "Todas as unidades". */
    guardarUnidade(null, null);
    definirUnidadeAtiva(null);

    if (!vinculo) {
        /* Demonstração: não há id de outra clínica para escolher. */
        cache = { ...cache, clinica: idOuNome };
        return cache;
    }

    lembrarClinica(vinculo.clinicaId);
    cache = { ...cache, clinica: vinculo.clinica, clinicaId: vinculo.clinicaId };
    acompanhar(cache);
    return cache;
}

/**
 * Escolhe a unidade da topbar; `null` é "Todas as unidades" — trilha T7.
 *
 * Recorta o que a tela mostra e onde o registro novo nasce. Não muda acesso:
 * quem recusa é a RLS, por clínica.
 */
export function trocarUnidade(id: string | null): void {
    const clinica = cache && cache.perfil === 'clinica' && !cache.impersonando ? cache.clinicaId : null;
    guardarUnidade(clinica, id);
    definirUnidadeAtiva(clinica ? id : null);
}
